import { create } from "zustand";

/**
 * Sinais de estado por símbolo (marketdata#25): fase de negociação (evento
 * SignalR "SS"), preço teórico de abertura ("TP") e contratos em aberto ("OI").
 *
 * Mesmo desenho do referentialPriceStore (#437): carona na assinatura de
 * quote, full-state idempotente, chave EXATA (o feed re-emite keyed FUT/FUL;
 * resolução de família aqui casaria contrato expirado no rollover), store
 * isolado — nunca no chartStore nem no cache do quoteWorker.
 *
 * Guards de ordenação:
 * - "OI" carrega data no payload (TS) → descarta out-of-order (o acervo HH= é
 *   re-despejado a cada reconexão e pode trazer o de ontem).
 * - "SS" e "TP" não têm data no wire → last-writer-wins (o stream é ordenado
 *   no servidor; snapshot chega antes do live por desenho).
 */
export const useStateSignalsStore = create((set) => ({
  // { [symbol]: { state, time, h } } — state é TEXTO do Feeder ("NEGOCIACAO",
  // "LEILAO", "PRE ABERTURA", …), não código: exibir como veio (com trim — o
  // Feeder emite " NEGOCIACAO" com espaço à esquerda na fase S).
  phaseBySymbol: {},
  // { [symbol]: { p, q, v, h } }
  theoreticalBySymbol: {},
  // { [symbol]: { v, ts, h } }
  openInterestBySymbol: {},

  applySymbolState: (msg) => {
    if (!msg || !msg.S) return;
    set((s) => ({
      phaseBySymbol: {
        ...s.phaseBySymbol,
        // time cru do wire: TimeSpan viaja como ticks Int64 no msgpack — sem
        // consumidor hoje; converter só quando alguém for exibir.
        [msg.S]: {
          state: String(msg.ST ?? "").trim(),
          time: msg.T ?? null,
          h: !!msg.H,
        },
      },
    }));
  },

  applyTheoreticalOpening: (msg) => {
    if (!msg || !msg.S) return;
    set((s) => ({
      theoreticalBySymbol: {
        ...s.theoreticalBySymbol,
        // Number(): defensivo contra decimal→string do msgpack (o DTO já foi
        // alinhado para float no servidor, mas payload antigo/estranho não
        // pode matar a linha silenciosamente).
        [msg.S]: {
          p: Number(msg.P),
          q: Number(msg.Q),
          v: Number(msg.V),
          h: !!msg.H,
        },
      },
    }));
  },

  applyOpenInterest: (msg) => {
    if (!msg || !msg.S) return;
    const ts = msg.TS ? new Date(msg.TS).getTime() : 0;
    if (!Number.isFinite(ts)) return; // TS inválido não entra nem clobra
    set((s) => {
      const current = s.openInterestBySymbol[msg.S];
      if (current && ts < current.ts) return s; // out-of-order: mantém o mais novo
      return {
        openInterestBySymbol: {
          ...s.openInterestBySymbol,
          [msg.S]: { v: Number(msg.V), ts, h: !!msg.H },
        },
      };
    });
  },

  clear: () =>
    set({
      phaseBySymbol: {},
      theoreticalBySymbol: {},
      openInterestBySymbol: {},
    }),
}));

/**
 * Leilão ativo? Gate da linha "Teórico" (decisão 04/08: teórico fora de leilão
 * é stale — a B3 não emite "fim do teórico").
 *
 * Allowlist EXATA derivada da tabela do Feeder (PhaseStatus.response()) — os
 * leilões reais da B3 nem sempre contêm "LEILAO" no texto: a pré-abertura
 * (Status 21, 9:45-10:00) e a chamada final de fechamento (Status 101) são
 * exatamente as janelas em que o teórico (OP=) é o dado principal. Ficam de
 * fora "POS LEILAO" (encerrado) e "LEILAO PROIBIDO" (não é leilão rodando).
 */
const AUCTION_STATES = new Set([
  "LEILAO", // 102
  "PRE LEILAO", // 106
  "LEILAO EXTENDIDO", // 119
  "LEILAO PRE ABERTURA", // Phase E
  "PRE ABERTURA", // 21 PRE_OPEN — leilão de abertura
  "CHAMADA FINAL FEC", // 101 — call de fechamento
  "FECHAMENTO", // Phase U — closing call
]);

export const isAuctionState = (state) => {
  if (!state) return false;
  return AUCTION_STATES.has(String(state).trim().toUpperCase());
};
